import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { useIdentity, usePayment } from '$api-clients/hooks';
import { useCompensationReportFilters } from '../../CompensationReportFilters/CompensationReportFilters.helpers';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import {
  buildReportingTransactionsQuery,
  compareByCloseDateThenAddress,
  DEFAULT_PAGE_SIZE,
  DealAllocationsTableColumns,
  type DealAllocationsTableRow,
  mapReportingTransactionToRow,
} from '../DealAllocationsTable.helpers';

interface UseGetDealAllocationsProps {
  sortModel: GridSortModel;
  viewReportAs: string;
}

interface UseGetDealAllocationsResult {
  rows: DealAllocationsTableRow[];
  loading: boolean;
  totalCount: number | undefined;
  rowCount: number | undefined;
  paginationModel: GridPaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
}

export function useGetDealAllocations({
  sortModel,
  viewReportAs,
}: UseGetDealAllocationsProps): UseGetDealAllocationsResult {
  const { filters } = useCompensationReportFilters();
  const allTimeStartDate = useAllTimeStartDate();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Reset to page 0 when filters change. Returns the same object reference when
  // already on page 0 so React bails out without a re-render, preventing a
  // double fetch.
  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [filters]);

  const query = useMemo(
    () =>
      buildReportingTransactionsQuery(
        filters,
        sortModel,
        viewReportAs,
        {
          offset: paginationModel.page * paginationModel.pageSize,
          limit: paginationModel.pageSize,
        },
        allTimeStartDate,
      ),
    [filters, sortModel, viewReportAs, paginationModel, allTimeStartDate],
  );

  const { data: transactionsData, isLoading: isLoadingTransactions } =
    usePayment('/reporting/transactions', { params: { query } });

  const results = transactionsData?.results ?? [];

  // Sort IDs for a stable SWR cache key regardless of result order.
  const userIds = useMemo(
    () =>
      [
        ...new Set(
          results.flatMap((r) =>
            r.teamMemberIdentityId ? [r.teamMemberIdentityId] : [],
          ),
        ),
      ].sort(),
    [results],
  );
  const teamIds = useMemo(
    () =>
      [
        ...new Set(
          results.flatMap((r) => (r.teamIdentityId ? [r.teamIdentityId] : [])),
        ),
      ].sort(),
    [results],
  );

  // DEFAULT_PAGE_SIZE is 25, so at most 25 unique IDs per page — no chunking needed.
  // Pass null as options (not path) to conditionally skip the fetch.
  const { data: usersData, isLoading: isLoadingUsers } = useIdentity(
    '/users',
    userIds.length > 0
      ? { params: { query: { ids: userIds.join(',') } } }
      : null,
  );
  const { data: teamsData, isLoading: isLoadingTeams } = useIdentity(
    '/teams',
    teamIds.length > 0
      ? { params: { query: { ids: teamIds.join(',') } } }
      : null,
  );

  const usersById = useMemo(
    () => Object.fromEntries((usersData?.data ?? []).map((u) => [u.id, u])),
    [usersData],
  );
  const teamsById = useMemo(
    () => Object.fromEntries((teamsData?.data ?? []).map((t) => [t.id, t])),
    [teamsData],
  );

  const rows = useMemo(() => {
    const primarySortField = sortModel?.[0]?.field;
    const isCloseDateSort =
      !primarySortField ||
      primarySortField === DealAllocationsTableColumns.closeDate;
    const sortedResults = isCloseDateSort
      ? [...results].sort(compareByCloseDateThenAddress)
      : results;
    return sortedResults.map((r) =>
      mapReportingTransactionToRow(r, { usersById, teamsById }),
    );
  }, [results, usersById, teamsById, sortModel]);

  const isLoading = isLoadingTransactions || isLoadingUsers || isLoadingTeams;

  return {
    rows,
    loading: isLoading,
    totalCount: transactionsData?.totalCount,
    rowCount: results.length,
    paginationModel,
    setPaginationModel,
  };
}
